import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from '@mui/material';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, mq } = theme.custom;
    return {
        root: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(var(--kv-min, 180px), 1fr))',
            gap: '16px 24px',
            margin: 0,
            [mq.xs]: { gridTemplateColumns: '1fr', gap: 12 },
        },
        stacked: { gridTemplateColumns: '1fr', gap: 12 },
        item: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
        label: { fontSize: 12, fontWeight: 500, color: color.text.secondary, margin: 0 },
        value: {
            fontSize: 14,
            color: color.text.primary,
            margin: 0,
            overflowWrap: 'anywhere',
            fontVariantNumeric: 'tabular-nums',
            minHeight: 20,
        },
        mono: { fontFamily: theme.custom.font.mono, fontSize: 13 },
    };
});

export interface KeyValueItem {
    label: React.ReactNode;
    value: React.ReactNode;
    /** Identifiers and codes: monospace. */
    mono?: boolean;
}

export interface KeyValueListProps {
    items: KeyValueItem[];
    /** Minimum column width; columns are added as the container allows. */
    minColumnWidth?: number;
    /** One item per row (sidebars). */
    stacked?: boolean;
    className?: string;
}

/** Label-over-value facts in a responsive grid; the standard way to show a record's fields. */
const KeyValueList = ({ items, minColumnWidth = 180, stacked = false, className }: KeyValueListProps) => {
    const classes = useStyles();
    return (
        <dl className={classNames(classes.root, { [classes.stacked]: stacked }, className)} style={{ '--kv-min': `${minColumnWidth}px` } as React.CSSProperties}>
            {items.map((item, index) => (
                <div className={classes.item} key={index}>
                    <dt className={classes.label}>{item.label}</dt>
                    <dd className={classNames(classes.value, { [classes.mono]: item.mono })}>
                        {item.value === null || item.value === undefined || item.value === '' ? '—' : item.value}
                    </dd>
                </div>
            ))}
        </dl>
    );
};

export default KeyValueList;
